/**
 * Console data plumbing on top of `@/api`: paged lists, query mapping to uc-core's query DTOs,
 * mutations that toast the backend's own message and refresh what they touched.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { api, isApiError } from '@/api'
import type { Query } from '@/api/client'
import { useToast } from '@/ui'
import { obj } from './mappers'

export interface Page<T> {
  rows: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  /** Totals over the whole filtered list, when the endpoint sends them. */
  summary?: Record<string, unknown>
}

export interface PageParams {
  page?: number
  limit?: number
}

/**
 * Filters → query string for uc-core: drops empty values and the UI's 'ALL'; booleans and
 * numbers become strings (the backend's ValidationPipe converts them back).
 */
export function toQuery(filters: object | undefined): Query {
  const out: Query = {}
  if (!filters) return out
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '' || v === 'ALL') continue
    if (Array.isArray(v)) {
      if (v.length) out[k] = v as ReadonlyArray<string | number>
      continue
    }
    if (typeof v === 'boolean') out[k] = v ? 'true' : 'false'
    else if (typeof v === 'number' || typeof v === 'string') out[k] = v
  }
  return out
}

/** GET a paginated uc-core list and map each row. */
export async function getPage<T>(
  path: string,
  filters: object | undefined,
  map: (row: Record<string, unknown>) => T,
): Promise<Page<T>> {
  const { data, meta, summary } = await api.paginated<Record<string, unknown>>(path, {
    query: toQuery(filters),
  })
  return {
    rows: data.map((r) => map(obj(r))),
    total: meta.total,
    page: meta.page,
    limit: meta.limit,
    totalPages: meta.totalPages,
    ...(summary ? { summary } : {}),
  }
}

/** GET a plain array endpoint and map each row. */
export async function getList<T>(
  path: string,
  filters: object | undefined,
  map: (row: Record<string, unknown>) => T,
): Promise<T[]> {
  const data = await api.get<unknown>(path, { query: toQuery(filters) })
  const list = Array.isArray(data) ? data : Array.isArray(obj(data).data) ? obj(data).data : []
  return (list as unknown[]).map((r) => map(obj(r)))
}

/** GET one object and map it. */
export async function getOne<T>(
  path: string,
  map: (row: Record<string, unknown>) => T,
  filters?: object,
): Promise<T> {
  const data = await api.get<unknown>(path, { query: toQuery(filters) })
  return map(obj(data))
}

/** Query hook for paged lists: keeps the previous page on screen while the next one loads. */
export function usePageQuery<T>(
  key: QueryKey,
  fetcher: () => Promise<Page<T>>,
  options: Partial<UseQueryOptions<Page<T>>> = {},
) {
  return useQuery<Page<T>>({
    queryKey: key,
    queryFn: fetcher,
    placeholderData: keepPreviousData,
    ...options,
  })
}

/** Readable message for any thrown error: uc-core's own message first. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) {
    if (e.kind === 'network') return 'No connection to the server.'
    if (e.kind === 'timeout') return 'The server took too long to answer.'
    const issues = e.issues
      ?.map((i: unknown) => {
        if (typeof i === 'string') return i
        const o = obj(i)
        const msg = typeof o.message === 'string' ? o.message : ''
        return typeof o.field === 'string' && o.field ? `${o.field}: ${msg}` : msg
      })
      .filter(Boolean)
      .join('; ')
    return issues && (e.kind === 'validation' || e.status === 422) ? `${e.message} — ${issues}` : e.message
  }
  if (e instanceof Error) return e.message
  return 'Something went wrong.'
}

/** Extra fields uc-core puts on an error body (409 blockers, open exception ids …), if any. */
export function errorDetail(e: unknown): Record<string, unknown> {
  if (!isApiError(e)) return {}
  return obj(e.body)
}

export interface ConsoleMutationOptions<TVars, TData> {
  mutationFn: (vars: TVars) => Promise<TData>
  /** Query-key prefixes to refetch after success (e.g. [['gate','lots']]). */
  invalidate?: QueryKey[] | ((data: TData, vars: TVars) => QueryKey[])
  /** Toast title on success. Omit for silent. */
  success?: string | ((data: TData, vars: TVars) => string)
  /** Toast title on error (the backend message is the description). */
  errorTitle?: string
  /** Return true to suppress the default error toast (the caller shows it inline). */
  onError?: (e: unknown, vars: TVars) => boolean | void
  onSuccess?: (data: TData, vars: TVars) => void
}

/**
 * useMutation with the console conventions: on success invalidate the touched resources and toast;
 * on error toast the server's message (unless the caller handles it).
 */
export function useConsoleMutation<TVars = void, TData = unknown>(
  opts: ConsoleMutationOptions<TVars, TData>,
) {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation<TData, unknown, TVars>({
    mutationFn: opts.mutationFn,
    onSuccess: async (data, vars) => {
      const keys =
        typeof opts.invalidate === 'function' ? opts.invalidate(data, vars) : opts.invalidate
      if (keys) await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })))
      const msg = typeof opts.success === 'function' ? opts.success(data, vars) : opts.success
      if (msg) toast.success(msg)
      opts.onSuccess?.(data, vars)
    },
    onError: (e, vars) => {
      if (opts.onError?.(e, vars) === true) return
      toast.error(opts.errorTitle ?? 'Could not save', errorMessage(e))
    },
  })
}
